import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { StorageService } from '../services/StorageService';

const { FiDownload, FiTrash2, FiBookOpen, FiHeart, FiEdit3, FiFeather } = FiIcons;

const Summary = () => {
  const [allReflections, setAllReflections] = useState({});
  const [exportFormat, setExportFormat] = useState('txt');

  useEffect(() => {
    const data = StorageService.getAllReflections();
    setAllReflections(data);
  }, []);

  const handleExport = () => {
    const exportText = StorageService.formatForExport(allReflections, exportFormat);
    const mimeType = exportFormat === 'md' ? 'text/markdown' : 'text/plain';
    const filename = `refine-release-journey.${exportFormat}`;
    const blob = new Blob([exportText], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all your data? This cannot be undone.')) {
      StorageService.clearAllData();
      setAllReflections({});
    }
  };

  const hasAnyData = Object.keys(allReflections).some(lesson => 
    Object.values(allReflections[lesson]).some(value => value && value.trim())
  );

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="text-center py-12 px-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-soft paper-texture"
      >
        <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-medium">
          <FiFeather className="text-white text-2xl" />
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-primary mb-6 leading-tight">
          My Refine + Release Journey
        </h1>
        <p className="font-sans text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
          This page displays all your saved reflections and readiness notes from the 8 lessons. Your journey of creative stewardship, preserved for reflection and growth.
        </p>
      </motion.div>

      {/* Export Options */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.1 }} 
        className="bg-white rounded-2xl shadow-soft p-8 paper-texture"
      >
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDownload} className="text-primary text-xl" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-primary font-semibold">Export Your Journey</h2>
              <p className="font-sans text-sm text-gray-500 mt-1">Keep a permanent record of your growth</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="font-sans text-sm text-gray-600 font-medium">Format:</label>
              <select 
                value={exportFormat} 
                onChange={(e) => setExportFormat(e.target.value)} 
                className="px-4 py-2 border border-gray-300 rounded-lg font-sans text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary literary-input"
              >
                <option value="txt">Text (.txt)</option>
                <option value="md">Markdown (.md)</option>
              </select>
            </div>
            <button 
              onClick={handleExport} 
              disabled={!hasAnyData} 
              className="bg-primary text-white px-6 py-3 rounded-xl font-sans font-semibold hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 btn-hover shadow-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiDownload} />
              Export
            </button>
          </div>
        </div>
        {!hasAnyData && (
          <div className="text-center py-8">
            <p className="font-sans text-gray-500 italic">No reflections saved yet. Complete some lessons to see your journey here.</p>
          </div>
        )}
      </motion.div>

      {/* Reflections Display */}
      {hasAnyData && (
        <div className="space-y-6">
          {Object.entries(allReflections).map(([lessonNumber, data], index) => {
            const hasData = Object.values(data).some(value => value && value.trim());
            if (!hasData) return null;
            
            return (
              <motion.div 
                key={lessonNumber} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.2 + index * 0.1 }} 
                className="bg-white rounded-2xl shadow-soft p-8 paper-texture"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiBookOpen} className="text-accent text-xl" />
                  </div>
                  <h3 className="font-serif text-2xl text-primary font-semibold">
                    Lesson {lessonNumber}
                  </h3>
                </div>
                <div className="space-y-6">
                  {Object.entries(data).map(([key, value]) => {
                    if (!value || !value.trim()) return null;
                    return (
                      <div key={key} className="border-l-4 border-primary/20 pl-6">
                        <h4 className="font-sans font-semibold text-gray-700 mb-3 text-lg capitalize">
                          {key.replace(/lesson\d+_/, '').replace(/_/g, ' ')}
                        </h4>
                        <p className="font-sans text-gray-600 leading-relaxed whitespace-pre-wrap">
                          {value}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Clear Data Button */}
      {hasAnyData && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.4 }} 
          className="flex justify-center"
        >
          <button 
            onClick={handleClearData} 
            className="bg-white text-gray-700 px-8 py-4 rounded-xl font-sans font-semibold hover:bg-red-50 hover:text-red-700 transition-all duration-300 flex items-center gap-3 btn-hover shadow-soft border border-gray-200"
          >
            <SafeIcon icon={FiTrash2} className="text-xl" />
            Clear All Data
          </button>
        </motion.div>
      )}

      {/* Encouragement */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.5 }} 
        className="text-center py-12 px-8 bg-gradient-to-r from-blue-50 via-orange-50 to-blue-50 rounded-2xl shadow-soft paper-texture"
      >
        <SafeIcon icon={FiHeart} className="text-4xl text-accent mx-auto mb-6" />
        <p className="font-serif text-xl text-gray-700 italic leading-relaxed max-w-2xl mx-auto">
          "Well done, good and faithful servant. You have been faithful with a few things; I will put you in charge of many things." - Matthew 25:23
        </p>
      </motion.div>
    </div>
  );
};

export default Summary;